
import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { isSuperUser } from "../config/roles";
import { useToast } from "../components/Toast";

// --- Sub-components Imports ---
import { DashboardTodosLiveDebugTab } from "../components/debug/DashboardTodosLiveDebugTab";
import { ApprovalDiagnosticsPanel } from "../components/debug/ApprovalDiagnosticsPanel";
import { ApprovalRepairTab } from "../components/debug/ApprovalRepairTab"; 
import { DataHealthPanel } from "../components/debug/DataHealthPanel";
import { FileUploadKit } from "../components/common/FileUploadKit";
import { analyzeFileV2, AiV2Response } from "../services/aiService"; 
import { uploadTempFile } from "../services/storageUpload";
import { FEATURES } from "../config/features";
import { DebugApprovalFlowsExporterPanel } from "../components/debug/DebugApprovalFlowsExporterPanel";

// --- Header: Build Info ---
function BuildInfoBanner() {
    const [info, setInfo] = useState<any>(null);
    useEffect(() => {
        fetch('/api/debug/build-info').then(r => r.json()).then(setInfo).catch(() => setInfo({error: "Failed to fetch"}));
    }, []);
    
    if (!info) return <div style={{fontSize:11, color:'#666'}}>Loading build info...</div>;
    return (
        <div style={{background:'#111', padding: '6px 12px', borderRadius: 4, marginBottom: 12, border: '1px solid #333', display:'flex', gap: 16, fontSize: 11, color:'#888'}}>
            <span>Commit: <b style={{color:'#fff'}}>{info.commitSha?.slice(0,7)}</b></span>
            <span>Branch: <b>{info.branch}</b></span>
            <span>Time: {info.buildTime}</span>
            <span>Env: {info.environment}</span>
        </div>
    );
}

// --- Tab: Automated Jobs Monitor ---
function AutomatedJobsTab() {
    return <div style={{color:'#888'}}>Jobs Monitor Placeholder (Restored)</div>;
}

// --- Tab: AI V2 Debug ---
function AiV2DebugSection() {
    const [file, setFile] = useState<File|null>(null);
    const [result, setResult] = useState<AiV2Response|null>(null);
    const [loading, setLoading] = useState(false);
    
    const handleTest = async () => {
        if(!file) return;
        setLoading(true);
        try {
            const meta = await uploadTempFile(file);
            const res = await analyzeFileV2({
                file: { name: meta.name, url: meta.url, size: meta.size },
                mode: 'extract'
            });
            setResult(res);
        } catch(e) { console.error(e); }
        finally { setLoading(false); }
    };
    
    return (
        <div className="section">
            <h3 className="h1">AI V2 測試 (Debug)</h3>
            <FileUploadKit onFilesSelected={fs => setFile(fs[0])} />
            <button className="btn" style={{marginTop:10}} onClick={handleTest} disabled={loading || !file}>
                {loading ? "Testing..." : "Upload & Analyze"}
            </button>
            {result && <pre style={{marginTop:10, maxHeight:300, overflow:'auto', background:'#111', padding:10}}>{JSON.stringify(result, null, 2)}</pre>}
        </div>
    );
}

// --- Main Page ---
export default function SystemDebugPage({ onNavigate }: { onNavigate: (key: string, id: string) => void }) {
    const { user } = useAuth();
    const [tab, setTab] = useState("LIVE");
    
    if (!isSuperUser(user?.email)) {
        return <div style={{padding: 40, color:'red'}}>403 Forbidden: Super User Access Required</div>;
    }

    return (
        <div style={{height: '100%', display:'flex', flexDirection:'column'}}>
            <BuildInfoBanner />
            
            <div className="row" style={{marginBottom: 16, borderBottom:'1px solid #333'}}>
                 <button className={`navbtn ${tab==='LIVE'?'active':''}`} onClick={()=>setTab('LIVE')}>Todos Live</button>
                 <button className={`navbtn ${tab==='HEALTH'?'active':''}`} onClick={()=>setTab('HEALTH')}>Data Health</button>
                 <button className={`navbtn ${tab==='APPROVAL'?'active':''}`} onClick={()=>setTab('APPROVAL')}>Approval Tools</button>
                 <button className={`navbtn ${tab==='AI_V2'?'active':''}`} onClick={()=>setTab('AI_V2')}>AI V2</button>
                 <button className={`navbtn ${tab==='JOBS'?'active':''}`} onClick={()=>setTab('JOBS')}>Jobs</button>
            </div>

            <div style={{flex: 1, overflowY:'auto'}}>
                 {tab === 'LIVE' && <DashboardTodosLiveDebugTab onNavigate={onNavigate} />}
                 {tab === 'HEALTH' && <DataHealthPanel />}
                 {tab === 'APPROVAL' && (
                     <div style={{display:'flex', flexDirection:'column', gap:24}}>
                         <ApprovalRepairTab />
                         <DebugApprovalFlowsExporterPanel />
                         <ApprovalDiagnosticsPanel />
                     </div>
                 )}
                 {tab === 'AI_V2' && <AiV2DebugSection />}
                 {tab === 'JOBS' && <AutomatedJobsTab />}
            </div>
        </div>
    );
}
